import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  CalendarClock,
  FileText,
  Film,
  Handshake,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Users,
  UserCog,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
const adminLogo = { url: "/media/investenergy-admin-logo.png" };
import { checkAdminAccess, getMe, logout } from "@/lib/api";
import { clearToken } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminLayout,
});

const NAV: { to: string; label: string; icon: typeof LayoutDashboard; exact?: boolean }[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/inscrieri", label: "Înscrieri", icon: Users },
  { to: "/admin/speakeri", label: "Speakeri", icon: UserCog },
  { to: "/admin/parteneri", label: "Parteneri", icon: Handshake },
  { to: "/admin/agenda", label: "Agendă", icon: CalendarClock },
  { to: "/admin/teme", label: "Teme dezbatere", icon: Lightbulb },
  { to: "/admin/gala", label: "Gala (video)", icon: Film },
  { to: "/admin/continut", label: "Conținut & texte", icon: FileText },
  { to: "/admin/setari", label: "Setări", icon: Settings },
];

function AdminLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("admin-sidebar-collapsed") === "1";
  });

  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("admin-sidebar-collapsed", next ? "1" : "0");
      }
      return next;
    });
  }

  const { data: isAdmin, isLoading } = useQuery({
    queryKey: ["is-admin"],
    queryFn: async () => {
      const access = await checkAdminAccess().catch(() => ({ blocked: false, ip: null }));
      if (access.blocked) return false;
      try {
        const { user } = await getMe();
        return user.role === "admin";
      } catch {
        return false;
      }
    },
  });

  async function signOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await logout().catch(() => {});
    clearToken();
    navigate({ to: "/auth", replace: true });
  }

  if (isLoading) {
    return <div className="p-10 text-sm text-muted-foreground">Se verifică accesul…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-xl font-semibold">Acces restricționat</h1>
        <p className="text-sm text-muted-foreground">
          Contul tău nu are drepturi de administrator.
        </p>
        <Button onClick={signOut} variant="outline">
          Deconectare
        </Button>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex min-h-screen bg-muted/30">
        {open ? (
          <div
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            onClick={() => setOpen(false)}
          />
        ) : null}
        <aside
          className={`fixed inset-y-0 left-0 z-40 shrink-0 border-r border-white/10 bg-[#061019] p-3 transition-all duration-300 lg:static lg:translate-x-0 ${
            open ? "translate-x-0" : "-translate-x-full"
          } ${collapsed ? "w-64 lg:w-[76px]" : "w-64"}`}
        >
          <div className="flex items-center justify-between gap-2 px-1">
            <Link to="/" className={collapsed ? "lg:hidden" : ""}>
              <img
                src={adminLogo.url}
                alt="InvesTenergy"
                className="h-8 w-auto brightness-0 invert"
              />
            </Link>
            <button
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Deschide meniul" : "Închide meniul"}
              className="hidden rounded-md p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white lg:block"
            >
              {collapsed ? (
                <PanelLeftOpen className="h-5 w-5" />
              ) : (
                <PanelLeftClose className="h-5 w-5" />
              )}
            </button>
            <button className="p-2 text-white/60 lg:hidden" onClick={() => setOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="mt-8 space-y-1">
            {NAV.map((item) => {
              const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
              const Icon = item.icon;
              const link = (
                <Link
                  key={item.to}
                  to={item.to as never}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    collapsed ? "lg:justify-center lg:px-0" : ""
                  } ${
                    active ? "bg-energy text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className={collapsed ? "lg:hidden" : ""}>{item.label}</span>
                </Link>
              );
              if (!collapsed) return link;
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </nav>

          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={signOut}
                  className="mt-8 flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Deconectare</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={signOut}
              className="mt-8 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-4 w-4" /> Deconectare
            </button>
          )}
        </aside>

        <div className="min-w-0 flex-1">
          <header className="flex items-center gap-3 border-b border-border bg-background px-4 py-3 lg:hidden">
            <button onClick={() => setOpen(true)} aria-label="Meniu">
              <Menu className="h-5 w-5" />
            </button>
            <span className="text-sm font-semibold">Administrare</span>
          </header>
          <main className="w-full px-4 py-8 sm:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
